/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col, Tabs, Tab, Button } from "@nordcloud/gnui";
import { useApplicationFormDataQuery } from "~/generated/graphql";
import { BreadcrumbsBox, GlobalSpinner } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { useAddApplication } from "~/hooks/graphql";
import { showError, showSuccess } from "~/services/toast";
import { generateActionErrorText } from "~/tools";
import {
  ApplicationDetailsForm,
  FormData as DetailsData,
} from "./ApplicationDetailsForm";
import { ApplicationEnvironmentsForm } from "./ApplicationEnvironmentsForm";
import { Summary } from "./SummaryTab";
import { Env } from "./types";

export function AddApplicationPage() {
  const [isCurrentStep, setCurrentStep] = React.useState(0);
  const [environments, setEnvironments] = React.useState<Env[]>([]);
  const [appId, setAppId] = React.useState<string | null>(null);

  const [addApplication, { loading }] = useAddApplication();

  const { data: applicationData } = useApplicationFormDataQuery({
    skip: appId == null,
    variables: { id: appId ?? "" },
  });

  React.useEffect(() => {
    const env = applicationData?.application?.environments;
    setEnvironments((env ?? []) as Env[]);
  }, [applicationData]);

  const goToNextStep = () => {
    setCurrentStep(isCurrentStep + 1);
  };

  const handleDetailsSubmit = async (details: DetailsData) => {
    try {
      const newApplication = await addApplication({
        variables: details,
      });
      const appNid = newApplication?.data?.addApplicationV2?.id;

      if (appNid == null) {
        showError(ERROR_TEXT.default);
        return;
      }

      setAppId(appNid);

      goToNextStep();
      showSuccess(SUCCESS_TEXT.applicationAdded);
    } catch (error) {
      showError(generateActionErrorText(error.message), {
        autoClose: false,
      });
    }
  };

  const clear = () => {
    setCurrentStep(0);
  };

  return (
    <>
      <GlobalSpinner loading={loading} />
      <Row>
        <Col>
          <BreadcrumbsBox title="Create New Application" />
          <Tabs wizard step={isCurrentStep} handleTab={setCurrentStep}>
            <Tab
              disabled
              heading="Application Details"
              caption="give some general information about your application"
              buttonsJustify="center"
              buttons={
                <Button form="add-application-form" type="submit">
                  Create Application
                </Button>
              }
            >
              <ApplicationDetailsForm
                id="add-application-form"
                onSubmit={handleDetailsSubmit}
              />
            </Tab>
            <Tab
              disabled
              heading="Application Environments"
              caption="add environments to your application, setup rules for the cost allocation and automate applications discovery"
              buttonsJustify="center"
              buttons={<Button onClick={goToNextStep}>Finish</Button>}
            >
              <ApplicationEnvironmentsForm
                applicationId={appId ?? ""}
                ownerId={applicationData?.application?.owner?.id || ""}
                environments={environments}
                onSkip={goToNextStep}
              />
            </Tab>
            <Tab
              disabled
              heading="Summary"
              caption="great, your application is ready"
            >
              <Summary applicationId={appId} clearHandler={clear} />
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </>
  );
}
